import CloseIcon from "@mui/icons-material/CloseRounded";
import CopyIcon from "@mui/icons-material/ContentCopyRounded";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";
import { useAlertContext } from "@shared";

import { Trans, useTranslation } from "@/translations";

import { MAIL_MERGE_TEXT_EMPHASIS } from "./MailMergeTextEmphasis";

interface Props {
  /** The configured path, offered for copying so it can be pasted into the picker's address bar. */
  expectedFolderPath: string;
  /**
   * The folder picked last, when it was refused. Present means the dialog is showing a rejected
   * attempt rather than a first one — the user retries from here instead of starting over.
   */
  rejectedFolderName: string | null;
  isSelecting: boolean;
  onSelect: () => void;
  onCancel: () => void;
}

/**
 * Asks the user to release one specific folder, and says so before the native picker opens — the
 * browser cannot navigate there on its own, and a folder released by mistake fails silently in Word
 * rather than visibly here.
 */
export function MailMergeFolderDialog({
  expectedFolderPath,
  rejectedFolderName,
  isSelecting,
  onSelect,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();

  const copyPath = async () => {
    try {
      await navigator.clipboard.writeText(expectedFolderPath);
      showSuccess(t("mailMerge.access.folderDialog.pathCopied"));
    } catch {
      showError(t("mailMerge.access.folderDialog.copyFailed"));
    }
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
        variant="h1"
      >
        {rejectedFolderName === null
          ? t("mailMerge.access.folderDialog.grantTitle")
          : t("mailMerge.access.folderDialog.mismatchTitle")}
        <IconButton onClick={onCancel} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 5, pt: 0 }}>
        <Stack spacing={2}>
          <Typography variant="body2">
            <Trans i18nKey="mailMerge.access.folderDialog.explanation" components={MAIL_MERGE_TEXT_EMPHASIS} />
          </Typography>

          {rejectedFolderName !== null && (
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: (theme) => theme.palette.fsw["grey-25"] }}>
              <BoxLabel color="text.secondary">{t("mailMerge.access.folderDialog.selectedLabel")}</BoxLabel>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontFamily: "monospace",
                  textDecoration: "line-through",
                  color: (theme) => theme.palette.fsw.red,
                }}
              >
                {rejectedFolderName}
              </Typography>
            </Box>
          )}

          {/* The focal point: the one folder that works, set apart so it survives a hurried read. */}
          <Box
            sx={{
              p: 2,
              border: 2,
              borderColor: "primary.main",
              borderRadius: 1,
              bgcolor: (theme) => theme.palette.fsw["blue-5"],
            }}
          >
            <BoxLabel color="primary.main">{t("mailMerge.access.folderDialog.targetLabel")}</BoxLabel>
            <Stack direction="row" alignItems="center" gap={1}>
              {/* Wraps rather than truncates: the user has to find this folder by hand, so every
                  character has to stay on screen — a UNC path outgrows one line. */}
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0, fontFamily: "monospace", wordBreak: "break-all" }}>
                {expectedFolderPath}
              </Typography>
              <IconButton
                size="small"
                aria-label={t("mailMerge.access.folderDialog.copyPath")}
                onClick={() => void copyPath()}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Alert severity="warning">{t("mailMerge.access.folderDialog.warning")}</Alert>

          <Typography variant="body2" color="text.secondary">
            {t("mailMerge.access.folderDialog.permissionHint")}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 5, pt: 0, justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" onClick={onSelect} disabled={isSelecting}>
          {t("mailMerge.access.folderDialog.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The uppercase caption SectionHeader established, tinted to say whether a box is the target or the refusal. */
function BoxLabel({ color, children }: { color: TypographyProps["color"]; children: string }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5, fontWeight: "bold", color }}
    >
      {children}
    </Typography>
  );
}
